"use client";

import React, { useState } from "react";
import { ChevronRight, ChevronLeft, Building2, DollarSign, FileText, CircleCheck, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RangeInput } from "@/components/ui/range-input";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const STEPS = [{
  id: 1,
  title: "Contact",
  icon: Building2
}, {
  id: 2,
  title: "Capital",
  icon: DollarSign
}, {
  id: 3,
  title: "Details",
  icon: FileText
}];
export default function CapitalRequestGateway() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="capital-request-gateway" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-20" />
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-border to-transparent opacity-10" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-border to-transparent opacity-10" />
      </div>

      <div ref={ref} className={`max-w-4xl mx-auto relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-white">
            Capital Request Gateway
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light tracking-wide">
            Institutional-grade financing for real estate and business growth. 
            Begin your capital structuring request below.
          </p>
        </div>

        <div className="border border-border/40 bg-card/30 backdrop-blur-md p-1 shadow-2xl">
          <div className="bg-background/80 p-6 md:p-10 border border-border/20">
            <FormProvider formId="capital_request_gateway_real_estate_capital" sectionName="capital-request-gateway" defaultValues={{
            full_name: "",
            company_name: "",
            email: "",
            phone: "",
            property_type: "",
            loan_amount: 2500000,
            financing_purpose: "",
            additional_details: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            property_type: {
              required: true
            },
            loan_amount: {
              required: true,
              min: 100000,
              max: 50000000
            },
            financing_purpose: {
              required: true
            },
            additional_details: {
              maxLength: 2000
            }
          }} submitText="Submit Request" successMessage="Request received. A capital advisor will review your structure shortly.">
              <MultiStepForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = STEPS.length;
  const progress = currentStep / totalSteps * 100;
  const nextStep = async () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleSubmit();
    }
  };
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-white">Request Submitted</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={() => {
        resetForm();
        setCurrentStep(1);
      }} variant="outline" className="mt-4 border-border/50 hover:bg-white/5">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={e => e.preventDefault()} className="space-y-8">
      {}
      <div className="space-y-4 mb-8">
        <div className="flex justify-between items-center px-1">
          {STEPS.map(step => {
          const Icon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          return <div key={step.id} className="flex flex-col items-center gap-2 relative z-10">
                <div className={`
                    size-10 rounded-full flex items-center justify-center border transition-all duration-300
                    ${isActive ? "bg-primary text-primary-foreground border-primary shadow-[0_0_15px_rgba(255,255,255,0.1)]" : isCompleted ? "bg-muted text-muted-foreground border-border" : "bg-transparent text-muted-foreground/30 border-border/30"}
                  `}>
                  <Icon className="size-4" />
                </div>
                <span className={`text-xs uppercase tracking-widest font-medium transition-colors duration-300 ${isActive ? "text-white" : "text-muted-foreground/50"}`}>
                  {step.title}
                </span>
              </div>;
        })}
        </div>
        <Progress value={progress} className="h-px bg-border/20" />
      </div>

      {}
      {currentStep === 1 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="full_name" rules={{
          required: "Full Name is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Full Name</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="Jane Doe" className="w-full bg-transparent border-b border-border py-3 text-base focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/20" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="company_name" render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Company / Sponsor</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="Dynestate Capital Partners" className="w-full bg-transparent border-b border-border py-3 text-base focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/20" />
                  </FormControl>
                  <FormDescription className="text-[10px] text-muted-foreground/50">Optional</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^\S+@\S+$/i,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
                  <FormControl>
                    <input {...field} type="email" placeholder="jane.doe@example.com" className="w-full bg-transparent border-b border-border py-3 text-base focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/20" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
          required: "Phone is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Phone Number</FormLabel>
                  <FormControl>
                    <input {...field} type="tel" placeholder="+1 (555) 123-4567" className="w-full bg-transparent border-b border-border py-3 text-base focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/20" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>}

      {}
      {currentStep === 2 && <div className="space-y-8 animate-in slide-in-from-right-4 fade-in duration-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField control={control} name="property_type" rules={{
          required: "Property Type is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Property Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full bg-transparent border-b border-border border-t-0 border-x-0 rounded-none px-0 py-3 h-auto focus:ring-0 focus:border-primary shadow-none">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {["Multifamily", "Office", "Retail", "Industrial", "Mixed-Use", "Other"].map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="financing_purpose" rules={{
          required: "Purpose is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Financing Purpose</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full bg-transparent border-b border-border border-t-0 border-x-0 rounded-none px-0 py-3 h-auto focus:ring-0 focus:border-primary shadow-none">
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {["Acquisition", "Refinance", "Construction", "Bridge Capital", "Other"].map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />
          </div>

          <FormField control={control} name="loan_amount" rules={{
        required: "Loan Amount is required",
        min: 100000,
        max: 50000000
      }} render={({
        field
      }) => <FormItem className="space-y-4 pt-4">
                <div className="flex justify-between items-end">
                  <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Requested Loan Amount (USD)</FormLabel>
                  <span className="text-2xl font-serif text-white">
                    ${Number(field.value || 0).toLocaleString()}
                  </span>
                </div>
                <FormControl>
                  <RangeInput min={100000} max={50000000} step={100000} value={Number(field.value) || 100000} onValueChange={val => field.onChange(val)} className="h-1 bg-border/30" />
                </FormControl>
                <div className="flex justify-between text-[10px] text-muted-foreground uppercase tracking-wider">
                  <span>$100k</span>
                  <span>$50M+</span>
                </div>
                <FormMessage />
              </FormItem>} />
        </div>}

      {}
      {currentStep === 3 && <div className="space-y-6 animate-in slide-in-from-right-4 fade-in duration-300">
          <FormField control={control} name="additional_details" rules={{
        maxLength: 2000
      }} render={({
        field
      }) => <FormItem className="space-y-2">
                <FormLabel className="text-xs uppercase tracking-widest text-muted-foreground">Additional Details</FormLabel>
                <FormControl>
                  <textarea {...field} placeholder="Provide any relevant project information, timelines, or special requirements." className="w-full min-h-[150px] bg-transparent border border-border p-4 text-base focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground/20 resize-none" />
                </FormControl>
                <FormDescription className="text-right text-xs text-muted-foreground">
                  {field.value?.length || 0} / 2000
                </FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>}

      {}
      <div className="flex justify-between pt-8 border-t border-border/10">
        <Button type="button" variant="ghost" onClick={prevStep} disabled={currentStep === 1 || isSubmitting} className={`text-muted-foreground hover:text-white ${currentStep === 1 ? 'invisible' : ''}`}>
          <ChevronLeft className="mr-2 size-4" /> Back
        </Button>

        <Button type="button" onClick={nextStep} disabled={isSubmitting} className="bg-white text-black hover:bg-white/90 min-w-[140px]">
          {isSubmitting ? "Processing..." : currentStep === totalSteps ? <>Submit Request <ArrowRight className="ml-2 size-4" /></> : <>Next Step <ChevronRight className="ml-2 size-4" /></>}
        </Button>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center animate-in fade-in">
          {submitError}
        </div>}
    </Form>;
}